import type { ControlMode, InputFrame } from './types';
import { movementPreset, type MovementAction, type MovementPreferences } from './movementBindings';

type MovementAxes = Pick<InputFrame, 'forward' | 'right' | 'turn' | 'up' | 'boost'>;

export function mapKeys(
  pressed: ReadonlySet<string>,
  context: { steering?: boolean; mode?: ControlMode; preferences?: MovementPreferences } = {}
): MovementAxes {
  const preset = movementPreset(false);
  const { bindings, leftHanded } = context.preferences ?? preset;
  const aliases: Partial<Record<MovementAction, string>> = {
    forward: 'ArrowUp',
    backward: 'ArrowDown',
    turnLeft: 'ArrowLeft',
    turnRight: 'ArrowRight',
    run: 'ShiftRight',
  };
  const held = (action: MovementAction): number =>
    Number(
      pressed.has(bindings[action]) ||
        (!leftHanded &&
          bindings[action] === preset.bindings[action] &&
          !Object.values(bindings).includes(aliases[action] ?? '') &&
          pressed.has(aliases[action] ?? ''))
    );
  const turning = held('turnRight') - held('turnLeft');
  const walking = !context.mode || context.mode === 'walk';
  const strafing = walking && !context.steering ? 0 : turning;
  return {
    forward: held('forward') - held('backward'),
    right: Math.sign(held('strafeRight') - held('strafeLeft') + strafing),
    turn: walking && !context.steering ? turning : 0,
    up: held('ascend') - held('descend'),
    boost: held('run') > 0,
  };
}

/**
 * A key pressed with Command, Control or Alt is a shortcut of the browser or the system, not a key
 * of the city: Shift-Command-B shows the bookmarks and boards no boat. Shift alone is the city's
 * own, for speed.
 */
export function isChord(e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey'>): boolean {
  return e.metaKey || e.ctrlKey || e.altKey;
}
